import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import CustomerCombobox from '../../components/CustomerCombobox.jsx';
import MasterCombobox from '../../components/MasterCombobox';
import DataLoggerSelector from '../../components/DataLoggerSelector';
import {
  Package,
  Users,
  FileText,
  Truck,
  ExternalLink,
  CheckCircle,
  Upload,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  Layers,
  MapPin,
  Calendar,
  Clock,
  ShieldCheck,
  Building2,
  Receipt,
  FileCheck2,
  Thermometer,
  Boxes,
  ArrowRight
} from 'lucide-react';

const PKG_TYPES = [
  'Box', 'Wooden Box', 'Carton Box', 'Gunny Bags', 'Plastic Bags',
  'Paper Bags', 'Drum', 'Pallet', 'Bottles', 'ODC', 'Others'
];
const STATUS_OPTS = ['Pickup', 'In Transit', 'Out for Delivery', 'Delivered', 'Pending'];
const INSURANCE_OPTS = ['Carrier Risk', 'Owner Risk'];

const FILE_SLOTS = [
  { key: 'invoice', label: 'Invoice', payloadKey: 'invoice_file_name' },
  { key: 'eway_bill', label: 'Eway Bill', payloadKey: 'eway_bill_file_name' },
  { key: 'pod', label: 'POD (Proof of Delivery)', payloadKey: 'pod_file_name' },
  { key: 'other', label: 'Other Document', payloadKey: 'other_file_name', hasNameField: true },
];

// Interactive Segmented Button Pill
const SegmentedPill = ({ options, value, onChange, testIdPrefix = 'pill', color = 'sky' }) => {
  const activeColorClasses = {
    sky: 'bg-sky-600 text-white shadow-xs',
    emerald: 'bg-emerald-600 text-white shadow-xs',
    blue: 'bg-blue-600 text-white shadow-xs',
    violet: 'bg-violet-600 text-white shadow-xs',
  }[color] || 'bg-sky-600 text-white shadow-xs';

  return (
    <div className="inline-flex p-0.5 bg-gray-100 rounded-lg border border-gray-200 text-xs font-ibm select-none">
      {options.map(opt => {
        const isSelected = value === opt;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`${testIdPrefix}-${opt.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => onChange(opt)}
            className={`px-3 py-1 rounded-md font-medium transition-all ${
              isSelected
                ? activeColorClasses
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
};

const F = ({ label, required, children, helper }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between">
      <Label className="text-[11px] font-semibold font-ibm text-gray-600 uppercase tracking-wide">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {helper && <span className="text-[10px] text-gray-400 font-ibm">{helper}</span>}
    </div>
    {children}
  </div>
);

const TInput = ({ testId, value, onChange, placeholder, type = 'text', readOnly, className = '' }) => (
  <Input
    data-testid={testId}
    type={type}
    value={value || ''}
    onChange={onChange}
    readOnly={readOnly}
    placeholder={placeholder}
    className={`h-9 text-sm border-gray-300 rounded-lg font-ibm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 bg-white ${className}`}
  />
);

const RoadRailV2Form = ({
  payload,
  onChange,
  customers,
  customerDetails = {},
  onCustomerSelect,
  locations = [],
  onFilesChange,
  storedDocuments = [],
  inventoryItems = [],
  onInventoryItemsChange,
  excludeDocketId,
}) => {
  const [estimates, setEstimates] = useState([]);
  const [fileObjects, setFileObjects] = useState({});
  const [activeSection, setActiveSection] = useState('sec-header');
  const [collapsedSections, setCollapsedSections] = useState({
    'sec-header': false,
    'sec-parties': false,
    'sec-package': false,
    'sec-invoice': false,
    'sec-vehicle': false,
  });

  // Fetch estimates
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!token || !base) return;
    fetch(`${base}/api/estimates?limit=100`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (d?.items) setEstimates(d.items);
      })
      .catch(() => {});
  }, []);

  const upd = useCallback(
    (section, field, value) => {
      onChange({ ...payload, [section]: { ...(payload[section] || {}), [field]: value } });
    },
    [payload, onChange]
  );

  // Auto-fill pickup_address from consignor's branch
  useEffect(() => {
    const cid = (payload.party_details || {}).consignor;
    if (!cid || !customerDetails[cid]) return;
    const branches = customerDetails[cid].branches || [];
    if (!branches.length) return;
    const b = branches.find(x => x.is_head_office) || branches[0];
    const addr = [b.address_line_1, b.address_line_2, b.city, b.state, b.pincode, b.country]
      .filter(Boolean)
      .join(', ');
    if (addr && !(payload.party_details || {}).pickup_address) {
      onChange({ ...payload, party_details: { ...(payload.party_details || {}), pickup_address: addr } });
    }
  }, [(payload.party_details || {}).consignor, customerDetails]); // eslint-disable-line

  // Auto-fill delivery_address from consignee's branch
  useEffect(() => {
    const cid = (payload.party_details || {}).consignee;
    if (!cid || !customerDetails[cid]) return;
    const branches = customerDetails[cid].branches || [];
    if (!branches.length) return;
    const b = branches.find(x => x.is_head_office) || branches[0];
    const addr = [b.address_line_1, b.address_line_2, b.city, b.state, b.pincode, b.country]
      .filter(Boolean)
      .join(', ');
    if (addr && !(payload.party_details || {}).delivery_address) {
      onChange({ ...payload, party_details: { ...(payload.party_details || {}), delivery_address: addr } });
    }
  }, [(payload.party_details || {}).consignee, customerDetails]); // eslint-disable-line

  const getBranches = cid => {
    if (!cid || !customerDetails[cid]) return [];
    return (customerDetails[cid].branches || []).map(b => ({
      id: b.id,
      name: b.branch_nick_name || b.city || 'Branch',
      subtext: [b.city, b.state].filter(Boolean).join(', '),
    }));
  };

  const locItems = locations.map(l => ({
    id: l.id,
    name: `${l.location_name}${l.location_code ? ' (' + l.location_code + ')' : ''}`,
    subtext: [l.city, l.country].filter(Boolean).join(', '),
  }));

  const h = payload.header || {};
  const p = payload.package_details || {};
  const pt = payload.party_details || {};
  const iv = payload.invoice_details || {};
  const vh = payload.vehicle_details || {};

  const hSet = (f, v) => upd('header', f, v);
  const pSet = (f, v) => upd('package_details', f, v);
  const ptSet = (f, v) => upd('party_details', f, v);
  const ivSet = (f, v) => upd('invoice_details', f, v);

  const ptMulti = useCallback(
    updates => {
      onChange({ ...payload, party_details: { ...(payload.party_details || {}), ...updates } });
    },
    [payload, onChange]
  );

  const getStoredDoc = label =>
    storedDocuments.find(d => d.document_title?.toLowerCase() === label.toLowerCase());

  const handleFileSelect = (slot, file) => {
    if (!file) return;
    const updated = { ...fileObjects, [slot.key]: { file, label: slot.label } };
    setFileObjects(updated);
    ivSet(slot.payloadKey, file.name);
    if (slot.key === 'pod') {
      hSet('docket_status', 'Delivered');
    }
    if (onFilesChange) onFilesChange(updated);
  };

  // Section navigation tabs
  const sections = useMemo(
    () => [
      { id: 'sec-header', label: 'Job & Booking', icon: ClipboardList, color: 'text-sky-600', border: 'border-l-sky-500' },
      { id: 'sec-parties', label: 'Party & Billing', icon: Users, color: 'text-amber-600', border: 'border-l-amber-500' },
      { id: 'sec-package', label: 'Package & Weight', icon: Package, color: 'text-emerald-600', border: 'border-l-emerald-500' },
      { id: 'sec-invoice', label: 'Invoice & E-Way', icon: FileText, color: 'text-violet-600', border: 'border-l-violet-500' },
      { id: 'sec-vehicle', label: 'Vehicle & Logger', icon: Truck, color: 'text-indigo-600', border: 'border-l-indigo-500' },
    ],
    []
  );

  const toggleSectionCollapse = id => {
    setCollapsedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAllSections = () => {
    const allCollapsed = Object.values(collapsedSections).every(Boolean);
    const nextState = {};
    sections.forEach(s => {
      nextState[s.id] = !allCollapsed;
    });
    setCollapsedSections(nextState);
  };

  const scrollToSection = id => {
    setCollapsedSections(prev => (prev[id] ? { ...prev, [id]: false } : prev));
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(id);
      }
    }, 50);
  };

  // Status color badge
  const getStatusBadge = status => {
    switch (status) {
      case 'Delivered':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'In Transit':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Out for Delivery':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Pickup':
        return 'bg-sky-100 text-sky-800 border-sky-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* ── Sticky Section Navigation Bar (Air V1 Style) ──────── */}
      <div className="sticky top-[68px] z-30 bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl px-3 py-2 shadow-sm flex items-center justify-between gap-2 overflow-x-auto transition-all">
        <div className="flex items-center gap-1.5 min-w-max">
          {sections.map(tab => {
            const Icon = tab.icon;
            const isActive = activeSection === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => scrollToSection(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold font-ibm transition-all select-none ${
                  isActive
                    ? 'bg-sky-600 text-white shadow-xs'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/90'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Right utility: Toggle Sections */}
        <div className="hidden md:flex items-center gap-2 pr-1 shrink-0 border-l border-gray-200 pl-3">
          <button
            type="button"
            onClick={toggleAllSections}
            className="text-[11px] font-medium text-gray-500 hover:text-sky-600 flex items-center gap-1 transition-colors select-none"
            title="Toggle compact / expanded views"
          >
            <Layers className="w-3.5 h-3.5 text-gray-400" />
            <span>Compact View</span>
          </button>
        </div>
      </div>

      {/* ── SECTION 1: JOB & BOOKING INFORMATION ─────────────── */}
      <Card
        id="sec-header"
        className="scroll-mt-32 bg-white rounded-xl border border-gray-200 shadow-xs hover:border-gray-300 transition-all overflow-hidden"
      >
        <div
          className={`px-5 py-3.5 flex items-center justify-between cursor-pointer select-none transition-colors ${
            collapsedSections['sec-header']
              ? 'bg-sky-50/40 hover:bg-sky-50/70 border-l-4 border-l-sky-500'
              : 'border-b-4 border-sky-500 bg-gray-50/30'
          }`}
          onClick={() => toggleSectionCollapse('sec-header')}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center">
              <ClipboardList className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-bold tracking-wide text-text-dark font-manrope uppercase">
                1. Job & Booking Information
              </h2>
              <p className="text-[11px] text-text-secondary font-ibm">
                Mode of transport, booking date, routing, and freight billing status
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {collapsedSections['sec-header'] && (
              <span className="text-xs font-ibm text-gray-600 bg-white border border-gray-200 px-2.5 py-0.5 rounded-full shadow-2xs">
                {h.mode || 'Surface'} · {h.service_type || 'General'} · {h.load_type || 'PTL'} ·{' '}
                {h.from_location || 'Origin'} ➔ {h.to_location || 'Destination'}
              </span>
            )}
            <button type="button" className="text-gray-400 hover:text-gray-600 p-1">
              {collapsedSections['sec-header'] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {!collapsedSections['sec-header'] && (
          <div className="p-5 space-y-4">
            {/* Row 1: Dates, Mode, Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <F label="Consignment Date" required>
                <TInput
                  testId="rrv2-date"
                  type="date"
                  value={h.date}
                  onChange={e => hSet('date', e.target.value)}
                />
              </F>

              <F label="Booking Date" required>
                <TInput
                  testId="rrv2-booking-date"
                  type="date"
                  value={h.booking_date}
                  onChange={e => hSet('booking_date', e.target.value)}
                />
              </F>

              <F label="Transport Mode" required>
                <div className="pt-0.5">
                  <SegmentedPill
                    options={['Surface', 'Air', 'Rail']}
                    value={h.mode || 'Surface'}
                    onChange={v => hSet('mode', v)}
                    testIdPrefix="rrv2-mode"
                    color="sky"
                  />
                </div>
              </F>

              <F label="Docket Status">
                <Select value={h.docket_status || 'Pending'} onValueChange={v => hSet('docket_status', v)}>
                  <SelectTrigger data-testid="rrv2-status" className="h-9 text-sm border-gray-300 rounded-lg font-ibm bg-white">
                    <SelectValue>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${getStatusBadge(h.docket_status || 'Pending')}`}>
                        {h.docket_status || 'Pending'}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTS.map(o => (
                      <SelectItem key={o} value={o}>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${getStatusBadge(o)}`}>
                          {o}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
            </div>

            {/* Row 2: Service Type, Origin / Destination, FTL/PTL, Freight Flag */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end pt-1">
              <F label="Service Type" required>
                <div className="pt-0.5">
                  <SegmentedPill
                    options={['General', 'Express', 'Refer']}
                    value={h.service_type || 'General'}
                    onChange={v => hSet('service_type', v)}
                    testIdPrefix="rrv2-service"
                    color="sky"
                  />
                </div>
              </F>

              <F label="Origin (From)" required>
                <MasterCombobox
                  items={locItems}
                  value={h.from_location}
                  onSelect={v => hSet('from_location', v)}
                  testId="rrv2-from"
                  placeholder="Select origin"
                  displayField="name"
                  subtextField="subtext"
                />
              </F>

              <F label="Destination (To)" required>
                <MasterCombobox
                  items={locItems}
                  value={h.to_location}
                  onSelect={v => hSet('to_location', v)}
                  testId="rrv2-to"
                  placeholder="Select destination"
                  displayField="name"
                  subtextField="subtext"
                />
              </F>

              <F label="Load Type" required>
                <div className="pt-0.5">
                  <SegmentedPill
                    options={['FTL', 'PTL']}
                    value={h.load_type || 'PTL'}
                    onChange={v => hSet('load_type', v)}
                    testIdPrefix="rrv2-ftl"
                    color="sky"
                  />
                </div>
              </F>

              <F label="Freight Billing Flag">
                <div className="pt-0.5">
                  <SegmentedPill
                    options={['TBB', 'FOD', 'PAID']}
                    value={h.billing_type || 'TBB'}
                    onChange={v => hSet('billing_type', v)}
                    testIdPrefix="rrv2-freight"
                    color="sky"
                  />
                </div>
              </F>
            </div>
          </div>
        )}
      </Card>

      {/* ── SECTION 2: PARTY AND BILLING DETAILS ─────────────── */}
      <Card
        id="sec-parties"
        className="scroll-mt-32 bg-white rounded-xl border border-gray-200 shadow-xs hover:border-gray-300 transition-all overflow-hidden"
      >
        <div
          className={`px-5 py-3.5 flex items-center justify-between cursor-pointer select-none transition-colors ${
            collapsedSections['sec-parties']
              ? 'bg-amber-50/40 hover:bg-amber-50/70 border-l-4 border-l-amber-500'
              : 'border-b-4 border-amber-500 bg-gray-50/30'
          }`}
          onClick={() => toggleSectionCollapse('sec-parties')}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center">
              <Users className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-bold tracking-wide text-text-dark font-manrope uppercase">
                2. Party & Billing Specifications
              </h2>
              <p className="text-[11px] text-text-secondary font-ibm">
                Shipper (Consignor), Consignee, Bill-to Account, and Delivery Schedule
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {collapsedSections['sec-parties'] && (
              <span className="text-xs font-ibm text-gray-600 bg-white border border-gray-200 px-2.5 py-0.5 rounded-full shadow-2xs">
                Shipper ➔ Consignee · Billing: Bill-To
              </span>
            )}
            <button type="button" className="text-gray-400 hover:text-gray-600 p-1">
              {collapsedSections['sec-parties'] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {!collapsedSections['sec-parties'] && (
          <div className="p-5 space-y-5">
            {/* Top 3 Column Party Cards: Consignor, Consignee, Bill To */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Box A: Consignor (Shipper) */}
              <div className="bg-gray-50/70 p-4 rounded-xl border border-gray-200 space-y-3">
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-xs font-bold text-gray-800 font-manrope uppercase flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-sky-600" />
                    Consignor (Shipper)
                  </span>
                  <span className="text-[10px] font-semibold text-sky-700 bg-sky-50 px-2 py-0.5 rounded border border-sky-200">
                    Origin Pickup
                  </span>
                </div>

                <F label="Consignor Company" required>
                  <CustomerCombobox
                    customers={customers}
                    value={pt.consignor}
                    onSelect={v => {
                      ptMulti({ consignor: v, consignor_branch: '', pickup_address: '' });
                      if (v) onCustomerSelect(v, 'consignor');
                    }}
                    testId="rrv2-consignor"
                    placeholder="Search Consignor..."
                  />
                </F>

                <F label="Branch" required>
                  <Select value={pt.consignor_branch || ''} onValueChange={v => ptSet('consignor_branch', v)}>
                    <SelectTrigger data-testid="rrv2-consignor-branch" className="h-9 text-sm border-gray-300 rounded-lg bg-white">
                      <SelectValue placeholder="Select Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {getBranches(pt.consignor).map(b => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>

                <F label="Pickup Address">
                  <Textarea
                    data-testid="rrv2-pickup-addr"
                    value={pt.pickup_address || ''}
                    onChange={e => ptSet('pickup_address', e.target.value)}
                    className="text-xs border-gray-300 min-h-[72px] font-ibm resize-none rounded-lg bg-white"
                    placeholder="Physical pickup address..."
                  />
                </F>

                <F label="Pickup Time">
                  <div className="relative">
                    <TInput
                      testId="rrv2-pickup-time"
                      type="time"
                      value={pt.pickup_time}
                      onChange={e => ptSet('pickup_time', e.target.value)}
                    />
                  </div>
                </F>
              </div>

              {/* Box B: Consignee (Receiver) */}
              <div className="bg-gray-50/70 p-4 rounded-xl border border-gray-200 space-y-3">
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-xs font-bold text-gray-800 font-manrope uppercase flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                    Consignee (Receiver)
                  </span>
                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                    Final Delivery
                  </span>
                </div>

                <F label="Consignee Company" required>
                  <CustomerCombobox
                    customers={customers}
                    value={pt.consignee}
                    onSelect={v => {
                      ptMulti({ consignee: v, consignee_branch: '', delivery_address: '' });
                      if (v) onCustomerSelect(v, 'consignee');
                    }}
                    testId="rrv2-consignee"
                    placeholder="Search Consignee..."
                  />
                </F>

                <F label="Branch" required>
                  <Select value={pt.consignee_branch || ''} onValueChange={v => ptSet('consignee_branch', v)}>
                    <SelectTrigger data-testid="rrv2-consignee-branch" className="h-9 text-sm border-gray-300 rounded-lg bg-white">
                      <SelectValue placeholder="Select Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {getBranches(pt.consignee).map(b => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>

                <F label="Delivery Address">
                  <Textarea
                    data-testid="rrv2-delivery-addr"
                    value={pt.delivery_address || ''}
                    onChange={e => ptSet('delivery_address', e.target.value)}
                    className="text-xs border-gray-300 min-h-[72px] font-ibm resize-none rounded-lg bg-white"
                    placeholder="Physical delivery address..."
                  />
                </F>

                <F label="Delivery Time">
                  <div className="relative">
                    <TInput
                      testId="rrv2-delivery-time"
                      type="time"
                      value={pt.delivery_time}
                      onChange={e => ptSet('delivery_time', e.target.value)}
                    />
                  </div>
                </F>
              </div>

              {/* Box C: Bill To Account & Commercial Settings */}
              <div className="bg-gray-50/70 p-4 rounded-xl border border-gray-200 space-y-3">
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-xs font-bold text-gray-800 font-manrope uppercase flex items-center gap-1.5">
                    <Receipt className="w-3.5 h-3.5 text-amber-600" />
                    Bill To & Estimates
                  </span>
                  <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                    Invoicing Entity
                  </span>
                </div>

                <F label="Bill To Customer" required>
                  <CustomerCombobox
                    customers={customers}
                    value={pt.bill_to}
                    onSelect={v => {
                      ptMulti({ bill_to: v, bill_to_branch: '' });
                      if (v) onCustomerSelect(v, 'bill_to');
                    }}
                    testId="rrv2-bill-to"
                    placeholder="Select Bill-To Customer..."
                  />
                </F>

                <F label="Bill To Branch">
                  <Select value={pt.bill_to_branch || ''} onValueChange={v => ptSet('bill_to_branch', v)}>
                    <SelectTrigger data-testid="rrv2-bill-to-branch" className="h-9 text-sm border-gray-300 rounded-lg bg-white">
                      <SelectValue placeholder="Select Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {getBranches(pt.bill_to).map(b => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>

                <F label="Billing Address">
                  <Textarea
                    data-testid="rrv2-bill-addr"
                    value={pt.bill_address || ''}
                    onChange={e => ptSet('bill_address', e.target.value)}
                    className="text-xs border-gray-300 min-h-[72px] font-ibm resize-none rounded-lg bg-white"
                    placeholder="Address for GST invoice..."
                  />
                </F>

                <div className="grid grid-cols-2 gap-2">
                  <F label="Link Estimate">
                    <Select value={pt.estimate_id || ''} onValueChange={v => ptSet('estimate_id', v)}>
                      <SelectTrigger data-testid="rrv2-estimate" className="h-9 text-xs border-gray-300 rounded-lg bg-white">
                        <SelectValue placeholder="Estimate..." />
                      </SelectTrigger>
                      <SelectContent>
                        {estimates.map(e => (
                          <SelectItem key={e.id} value={e.id} className="text-xs">
                            {e.estimate_no}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </F>

                  <F label="Expected Delivery">
                    <TInput
                      testId="rrv2-exp-dly-date"
                      type="date"
                      value={pt.expected_delivery_date}
                      onChange={e => ptSet('expected_delivery_date', e.target.value)}
                    />
                  </F>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* ── SECTION 3: PACKAGE & WEIGHT SPECIFICATIONS ────────── */}
      <Card
        id="sec-package"
        className="scroll-mt-32 bg-white rounded-xl border border-gray-200 shadow-xs hover:border-gray-300 transition-all overflow-hidden"
      >
        <div
          className={`px-5 py-3.5 flex items-center justify-between cursor-pointer select-none transition-colors ${
            collapsedSections['sec-package']
              ? 'bg-emerald-50/40 hover:bg-emerald-50/70 border-l-4 border-l-emerald-500'
              : 'border-b-4 border-emerald-500 bg-gray-50/30'
          }`}
          onClick={() => toggleSectionCollapse('sec-package')}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <Package className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-bold tracking-wide text-text-dark font-manrope uppercase">
                3. Package & Weight Specifications
              </h2>
              <p className="text-[11px] text-text-secondary font-ibm">
                Dimensions, package quantities, actual vs chargeable weight, and ODA
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {collapsedSections['sec-package'] && (
              <span className="text-xs font-ibm text-gray-600 bg-white border border-gray-200 px-2.5 py-0.5 rounded-full shadow-2xs">
                {p.no_of_packages || 0} Pkgs · Actual: {p.actual_weight || 0} KG · Charge: {p.charge_weight || 0} KG
              </span>
            )}
            <button type="button" className="text-gray-400 hover:text-gray-600 p-1">
              {collapsedSections['sec-package'] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {!collapsedSections['sec-package'] && (
          <div className="p-5 space-y-4">
            {/* Live Metrics Summary Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-emerald-50/50 p-3 rounded-xl border border-emerald-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xs">
                  <Boxes className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[10px] uppercase font-bold text-emerald-800 tracking-wider font-ibm">Total Pkgs</div>
                  <div className="text-sm font-extrabold text-emerald-950 font-manrope">{p.no_of_packages || 0}</div>
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xs">
                  KG
                </div>
                <div>
                  <div className="text-[10px] uppercase font-bold text-emerald-800 tracking-wider font-ibm">Actual Weight</div>
                  <div className="text-sm font-extrabold text-emerald-950 font-manrope">{p.actual_weight || 0} <span className="text-xs font-normal">kg</span></div>
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xs">
                  CW
                </div>
                <div>
                  <div className="text-[10px] uppercase font-bold text-emerald-800 tracking-wider font-ibm">Charge Weight</div>
                  <div className="text-sm font-extrabold text-emerald-950 font-manrope">{p.charge_weight || 0} <span className="text-xs font-normal">kg</span></div>
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xs">
                  CFT
                </div>
                <div>
                  <div className="text-[10px] uppercase font-bold text-emerald-800 tracking-wider font-ibm">Volumetric CFT</div>
                  <div className="text-sm font-extrabold text-emerald-950 font-manrope">{p.cft || '—'}</div>
                </div>
              </div>
            </div>

            {/* Row 1: Package Type, No of Pkgs, Description of Goods, CFT, Size */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
              <F label="Package Type">
                <Select value={p.package_type || ''} onValueChange={v => pSet('package_type', v)}>
                  <SelectTrigger data-testid="rrv2-pkg-type" className="h-9 text-sm border-gray-300 rounded-lg bg-white">
                    <SelectValue placeholder="Select Type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PKG_TYPES.map(o => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>

              <F label="No Of Packages">
                <TInput
                  testId="rrv2-no-pkg"
                  type="number"
                  value={p.no_of_packages}
                  onChange={e => pSet('no_of_packages', e.target.value)}
                  placeholder="Total count"
                />
              </F>

              <F label="Description Of Goods">
                <TInput
                  testId="rrv2-desc-goods"
                  value={p.description_of_goods}
                  onChange={e => pSet('description_of_goods', e.target.value)}
                  placeholder="Commodity / Cargo details"
                />
              </F>

              <F label="CFT (L × W × H)">
                <TInput
                  testId="rrv2-cft"
                  value={p.cft}
                  onChange={e => pSet('cft', e.target.value)}
                  placeholder="Calculated CFT"
                />
              </F>

              <F label="Size Dimension Unit">
                <div className="pt-0.5">
                  <SegmentedPill
                    options={['CMS', 'Inches', 'Foot']}
                    value={p.size_unit || 'CMS'}
                    onChange={v => pSet('size_unit', v)}
                    testIdPrefix="rrv2-size"
                    color="emerald"
                  />
                </div>
              </F>
            </div>

            {/* Row 2: Actual WT, Charge WT, ODA Delivery, ODA Location */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <F label="Actual WT. (KG)">
                <TInput
                  testId="rrv2-actual-wt"
                  type="number"
                  step="0.01"
                  value={p.actual_weight}
                  onChange={e => pSet('actual_weight', e.target.value)}
                  placeholder="Actual scale weight"
                />
              </F>

              <F label="Charge WT. (KG)">
                <TInput
                  testId="rrv2-charge-wt"
                  type="number"
                  step="0.01"
                  value={p.charge_weight}
                  onChange={e => pSet('charge_weight', e.target.value)}
                  placeholder="Billed chargeable weight"
                />
              </F>

              <F label="ODA Delivery Charges">
                <TInput
                  testId="rrv2-oda-delivery"
                  value={p.oda_delivery}
                  onChange={e => pSet('oda_delivery', e.target.value)}
                  placeholder="ODA charge amount / notes"
                />
              </F>

              <F label="ODA Location Name">
                <TInput
                  testId="rrv2-oda-location"
                  value={p.oda_location_name}
                  onChange={e => pSet('oda_location_name', e.target.value)}
                  placeholder="Out of delivery area point"
                />
              </F>
            </div>
          </div>
        )}
      </Card>

      {/* ── SECTION 4: INVOICE AND DOCUMENTATION ─────────────── */}
      <Card
        id="sec-invoice"
        className="scroll-mt-32 bg-white rounded-xl border border-gray-200 shadow-xs hover:border-gray-300 transition-all overflow-hidden"
      >
        <div
          className={`px-5 py-3.5 flex items-center justify-between cursor-pointer select-none transition-colors ${
            collapsedSections['sec-invoice']
              ? 'bg-violet-50/40 hover:bg-violet-50/70 border-l-4 border-l-violet-500'
              : 'border-b-4 border-violet-500 bg-gray-50/30'
          }`}
          onClick={() => toggleSectionCollapse('sec-invoice')}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-bold tracking-wide text-text-dark font-manrope uppercase">
                4. Commercial Invoice & E-Way Bill
              </h2>
              <p className="text-[11px] text-text-secondary font-ibm">
                Invoice details, transit insurance, E-Way Bill validity, and digital document uploads
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {collapsedSections['sec-invoice'] && (
              <span className="text-xs font-ibm text-gray-600 bg-white border border-gray-200 px-2.5 py-0.5 rounded-full shadow-2xs">
                Inv #{iv.invoice_no || '—'} · ₹{iv.invoice_value || '0'} · E-Way: {iv.eway_bill_no || '—'}
              </span>
            )}
            <button type="button" className="text-gray-400 hover:text-gray-600 p-1">
              {collapsedSections['sec-invoice'] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {!collapsedSections['sec-invoice'] && (
          <div className="p-5 space-y-4">
            {/* Row 1: Invoice No | Date | Invoice Value | Insurance */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <F label="Invoice No.">
                <TInput
                  testId="rrv2-inv-no"
                  value={iv.invoice_no}
                  onChange={e => ivSet('invoice_no', e.target.value)}
                  placeholder="GST Invoice number"
                />
              </F>

              <F label="Invoice Date">
                <TInput
                  testId="rrv2-inv-date"
                  type="date"
                  value={iv.date}
                  onChange={e => ivSet('date', e.target.value)}
                />
              </F>

              <F label="Invoice Value (₹)">
                <TInput
                  testId="rrv2-inv-value"
                  type="number"
                  step="0.01"
                  value={iv.invoice_value}
                  onChange={e => ivSet('invoice_value', e.target.value)}
                  placeholder="Total declared value"
                />
              </F>

              <F label="Transit Insurance">
                <Select value={iv.insurance || 'Carrier Risk'} onValueChange={v => ivSet('insurance', v)}>
                  <SelectTrigger data-testid="rrv2-insurance" className="h-9 text-sm border-gray-300 rounded-lg bg-white">
                    <SelectValue placeholder="Select Insurance..." />
                  </SelectTrigger>
                  <SelectContent>
                    {INSURANCE_OPTS.map(o => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
            </div>

            {/* Row 2: EWay Bill No | EWay Bill Issued Date | EWay Bill Expiry Date */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-gray-50/60 p-3.5 rounded-xl border border-gray-200">
              <F label="E-Way Bill No.">
                <TInput
                  testId="rrv2-eway-bill"
                  value={iv.eway_bill_no}
                  onChange={e => ivSet('eway_bill_no', e.target.value)}
                  placeholder="12-digit E-Way Bill No."
                />
              </F>

              <F label="E-Way Bill Issued Date">
                <TInput
                  testId="rrv2-eway-issued-date"
                  type="date"
                  value={iv.eway_bill_issued_date}
                  onChange={e => ivSet('eway_bill_issued_date', e.target.value)}
                />
              </F>

              <F label="E-Way Bill Expiry Date">
                <TInput
                  testId="rrv2-eway-expiry-date"
                  type="date"
                  value={iv.eway_bill_expiry_date}
                  onChange={e => ivSet('eway_bill_expiry_date', e.target.value)}
                />
              </F>
            </div>

            {/* Row 3: Digital File Attachments */}
            <div className="space-y-2 pt-1">
              <Label className="text-xs font-bold text-gray-700 font-manrope uppercase tracking-wider flex items-center gap-1.5">
                <FileCheck2 className="w-3.5 h-3.5 text-violet-600" />
                Attached Shipping Documents
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {FILE_SLOTS.map(slot => {
                  const uploaded = getStoredDoc(slot.label);
                  const pending = fileObjects[slot.key];
                  return (
                    <div
                      key={slot.key}
                      className="p-3 bg-gray-50/80 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-700 font-ibm">{slot.label}</span>
                        {uploaded ? (
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.2 rounded flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Saved
                          </span>
                        ) : pending ? (
                          <span className="text-[10px] font-bold text-sky-700 bg-sky-100 px-1.5 py-0.2 rounded">
                            Pending
                          </span>
                        ) : (
                          <span className="text-[10px] text-gray-400">Optional</span>
                        )}
                      </div>

                      {uploaded ? (
                        <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-ibm">
                          <a
                            href={uploaded.drive_view_link}
                            target="_blank"
                            rel="noreferrer"
                            className="text-emerald-700 font-medium truncate hover:underline flex items-center justify-between gap-1"
                          >
                            <span className="truncate">{uploaded.original_file_name}</span>
                            <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                          </a>
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          <label className="cursor-pointer flex items-center justify-center gap-1.5 py-2 px-3 border border-dashed border-gray-300 rounded-lg text-xs font-ibm bg-white hover:bg-gray-50 transition-colors text-gray-600">
                            <Upload className="w-3.5 h-3.5 text-gray-400" />
                            <span>{pending ? 'Replace File' : 'Upload File'}</span>
                            <input
                              type="file"
                              className="hidden"
                              onChange={e => handleFileSelect(slot, e.target.files[0])}
                            />
                          </label>
                          {pending && (
                            <p className="text-[11px] text-sky-700 font-ibm truncate px-1 font-medium">
                              {pending.file.name}
                            </p>
                          )}
                        </div>
                      )}

                      {slot.hasNameField && (
                        <TInput
                          testId="rrv2-other-doc-name"
                          value={iv.other_doc_name}
                          onChange={e => ivSet('other_doc_name', e.target.value)}
                          placeholder="Document title / description"
                          className="h-8 text-xs"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* ── SECTION 5: VEHICLE & TEMPERATURE MONITORING ──────── */}
      <Card
        id="sec-vehicle"
        className="scroll-mt-32 bg-white rounded-xl border border-gray-200 shadow-xs hover:border-gray-300 transition-all overflow-hidden"
      >
        <div
          className={`px-5 py-3.5 flex items-center justify-between cursor-pointer select-none transition-colors ${
            collapsedSections['sec-vehicle']
              ? 'bg-indigo-50/40 hover:bg-indigo-50/70 border-l-4 border-l-indigo-500'
              : 'border-b-4 border-indigo-500 bg-gray-50/30'
          }`}
          onClick={() => toggleSectionCollapse('sec-vehicle')}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center">
              <Truck className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-bold tracking-wide text-text-dark font-manrope uppercase">
                5. Vehicle & Temperature Monitoring
              </h2>
              <p className="text-[11px] text-text-secondary font-ibm">
                Vehicle registration, temperature monitoring, and inventory-linked Data Logger
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {collapsedSections['sec-vehicle'] && (
              <span className="text-xs font-ibm text-gray-600 bg-white border border-gray-200 px-2.5 py-0.5 rounded-full shadow-2xs">
                Veh #{vh.reg_no || '—'} · Logger: {vh.data_logger_no || 'None'} · Temp: {vh.temperature || '—'}
              </span>
            )}
            <button type="button" className="text-gray-400 hover:text-gray-600 p-1">
              {collapsedSections['sec-vehicle'] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {!collapsedSections['sec-vehicle'] && (
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
              <F label="Vehicle Registration No. (Reg No.)" helper="Assigned truck / carrier">
                <TInput
                  testId="rrv2-reg-no"
                  value={vh.reg_no}
                  onChange={e => upd('vehicle_details', 'reg_no', e.target.value)}
                  placeholder="e.g. MH-12-AB-1234"
                />
              </F>

              <div className="space-y-1">
                <label className="text-[11px] font-semibold font-ibm text-gray-600 uppercase tracking-wide">
                  Data Logger No.
                </label>
                <DataLoggerSelector
                  value={vh.data_logger_no}
                  loggerSource={vh.data_logger_source}
                  loggerItemId={vh.data_logger_item_id}
                  onChange={({ data_logger_no, data_logger_source, data_logger_item_id }) => {
                    onChange({
                      ...payload,
                      vehicle_details: {
                        ...(payload.vehicle_details || {}),
                        data_logger_no,
                        data_logger_source,
                        data_logger_item_id,
                      },
                    });
                  }}
                  inventoryItems={inventoryItems}
                  onInventoryItemsChange={onInventoryItemsChange}
                  excludeDocketId={excludeDocketId}
                />
              </div>

              <F label="Cargo Pickup Temperature" helper="Celsius / range">
                <div className="relative">
                  <TInput
                    testId="rrv2-temperature"
                    value={vh.temperature}
                    onChange={e => upd('vehicle_details', 'temperature', e.target.value)}
                    placeholder="e.g. +2°C to +8°C or -20°C"
                  />
                  <Thermometer className="w-4 h-4 text-gray-400 absolute right-3 top-2.5 pointer-events-none" />
                </div>
              </F>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default RoadRailV2Form;
